import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import type { WidgetDefinition } from '../services/dashboard';
import { Badge } from './ui/Badge';

interface DraggableWidgetOptionProps {
  widget: WidgetDefinition;
  onClick: () => void;
  disabled?: boolean;
}

/**
 * A draggable widget option for the widget picker.
 * Can be clicked to add immediately, or dragged to a specific location.
 */
const DraggableWidgetOption: React.FC<DraggableWidgetOptionProps> = ({
  widget,
  onClick,
  disabled = false,
}) => {
  const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
    id: `new-widget-${widget.id}`,
    disabled,
    data: {
      type: 'new-widget',
      widgetType: widget.id,
      widgetDef: widget,
    },
  });

  const style: React.CSSProperties = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: 1000,
      }
    : {};

  return (
    <div
      ref={setNodeRef}
      className={`widget-picker-option ${isDragging ? 'is-dragging' : ''}`}
      style={style}
      onClick={onClick}
      {...listeners}
      {...attributes}
    >
      <span className="widget-picker-icon">{widget.icon}</span>
      <div className="widget-picker-info">
        <h4>{widget.name}</h4>
        <p>{widget.description}</p>
      </div>
      <Badge variant="secondary" size="sm">
        {widget.default_width}x{widget.default_height}
      </Badge>
    </div>
  );
};

export default DraggableWidgetOption;
